import { useState } from 'react';
import {
  ActionIcon,
  Button,
  Center,
  Checkbox,
  Group,
  Loader,
  Modal,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconClock, IconEdit, IconPlus, IconTrash } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useSubscription } from 'urql';
import { ErrorAlert } from '@/components/error-alert';
import { getErrorMessage } from '@/lib/error-message';
import { useOrg } from '@/hooks/use-org';
import {
  BusinessHoursSubscription,
  CreateBusinessHoursMutation,
  DeleteBusinessHoursMutation,
  UpdateBusinessHoursMutation,
} from './-components/graphql';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'America/Phoenix', label: 'Arizona Time (MST)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
];

type TimeEntry = { days: string[]; openTime: string; closeTime: string };

type FormState = { name: string; timezone: string; entries: TimeEntry[] };

const EMPTY_ENTRY: TimeEntry = { days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'], openTime: '09:00', closeTime: '17:00' };

const EMPTY_FORM: FormState = { name: '', timezone: 'America/New_York', entries: [{ ...EMPTY_ENTRY }] };

export const Route = createFileRoute('/_authed/settings/business-hours')({
  component: SettingsBusinessHoursPage,
});

function parseEntries(entries: unknown): TimeEntry[] {
  if (!Array.isArray(entries)) return [];

  return entries.map((entry) => {
    const record = entry as Partial<TimeEntry>;

    return {
      days: Array.isArray(record.days) ? record.days : [],
      openTime: typeof record.openTime === 'string' ? record.openTime : '09:00',
      closeTime: typeof record.closeTime === 'string' ? record.closeTime : '17:00',
    };
  });
}

function TimeEntryRow({
  entry,
  onChange,
  onRemove,
}: {
  entry: TimeEntry;
  onChange: (entry: TimeEntry) => void;
  onRemove: () => void;
}) {
  return (
    <Paper p="sm" withBorder>
      <Stack gap="xs">
        <Group justify="space-between">
          <Text size="sm" fw={500}>
            Days
          </Text>
          <ActionIcon color="red" variant="subtle" onClick={onRemove}>
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
        <Checkbox.Group value={entry.days} onChange={(days) => onChange({ ...entry, days })}>
          <Group gap="xs">
            {DAYS.map((day) => (
              <Checkbox key={day} value={day} label={day} size="xs" />
            ))}
          </Group>
        </Checkbox.Group>
        <Group gap="sm">
          <TextInput
            label="Open"
            type="time"
            size="xs"
            value={entry.openTime}
            onChange={(event) => onChange({ ...entry, openTime: event.currentTarget.value })}
            style={{ flex: 1 }}
          />
          <TextInput
            label="Close"
            type="time"
            size="xs"
            value={entry.closeTime}
            onChange={(event) => onChange({ ...entry, closeTime: event.currentTarget.value })}
            style={{ flex: 1 }}
          />
        </Group>
      </Stack>
    </Paper>
  );
}

function ScheduleForm({ form, onChange }: { form: FormState; onChange: (form: FormState) => void }) {
  return (
    <Stack gap="md">
      <TextInput
        label="Schedule Name"
        placeholder="e.g., Sales Hours"
        value={form.name}
        onChange={(event) => onChange({ ...form, name: event.currentTarget.value })}
        required
      />
      <Select
        label="Timezone"
        data={TIMEZONE_OPTIONS}
        value={form.timezone}
        onChange={(value) => value && onChange({ ...form, timezone: value })}
        searchable
        required
      />
      <Stack gap="xs">
        <Text size="sm" fw={500}>
          Time Blocks
        </Text>
        {form.entries.map((entry, index) => (
          <TimeEntryRow
            key={index}
            entry={entry}
            onChange={(updated) =>
              onChange({ ...form, entries: form.entries.map((e, i) => (i === index ? updated : e)) })
            }
            onRemove={() => onChange({ ...form, entries: form.entries.filter((_, i) => i !== index) })}
          />
        ))}
        <Button
          variant="light"
          leftSection={<IconPlus size={16} />}
          onClick={() => onChange({ ...form, entries: [...form.entries, { ...EMPTY_ENTRY }] })}
        >
          Add Time Block
        </Button>
      </Stack>
    </Stack>
  );
}

function BusinessHours({ organizationId }: { organizationId: string }) {
  const [{ data }] = useSubscription({ query: BusinessHoursSubscription, variables: { organizationId } });
  const [, createSchedule] = useMutation(CreateBusinessHoursMutation);
  const [, updateSchedule] = useMutation(UpdateBusinessHoursMutation);
  const [, deleteSchedule] = useMutation(DeleteBusinessHoursMutation);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>({ ...EMPTY_FORM });
  const [error, setError] = useState<string | null>(null);

  if (!data) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, entries: [{ ...EMPTY_ENTRY }] });
    setModalOpen(true);
  };

  const openEdit = (schedule: (typeof data.businessHours)[number]) => {
    setEditingId(schedule.id);
    setForm({ name: schedule.name, timezone: schedule.timezone, entries: parseEntries(schedule.entries) });
    setModalOpen(true);
  };

  const save = async () => {
    setError(null);

    const variables = { name: form.name.trim(), timezone: form.timezone, entries: form.entries };
    const result = editingId
      ? await updateSchedule({ id: editingId, ...variables })
      : await createSchedule({ organizationId, ...variables });

    if (result.error) return setError(getErrorMessage(result.error, 'Failed to save the schedule'));

    setModalOpen(false);
  };

  const remove = async (id: string) => {
    setError(null);

    const result = await deleteSchedule({ id });

    setConfirmDeleteId(null);

    if (result.error) setError(getErrorMessage(result.error, 'Failed to delete the schedule'));
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <div>
          <Title order={3}>Business Hours</Title>
          <Text size="sm" c="dimmed">
            Define schedules that control when your phone lines are open or closed.
          </Text>
        </div>
        <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
          Create Schedule
        </Button>
      </Group>

      <ErrorAlert message={error} />

      {data.businessHours.length === 0 ? (
        <Paper p="xl" withBorder ta="center">
          <Stack align="center" gap="sm">
            <IconClock size={48} color="var(--mantine-color-dimmed)" />
            <Text c="dimmed">No business hours schedules yet.</Text>
            <Button variant="light" onClick={openCreate}>
              Create your first schedule
            </Button>
          </Stack>
        </Paper>
      ) : (
        <Stack gap="sm">
          {data.businessHours.map((schedule) => {
            const entries = parseEntries(schedule.entries);

            return (
              <Paper key={schedule.id} p="md" withBorder>
                <Group justify="space-between">
                  <div>
                    <Text fw={600}>{schedule.name}</Text>
                    <Text size="sm" c="dimmed">
                      {schedule.timezone} &middot; {entries.length} time block{entries.length !== 1 ? 's' : ''}
                    </Text>
                    <Text size="xs" c="dimmed" mt={4}>
                      {entries.map((e) => `${e.days.join(', ')} ${e.openTime}–${e.closeTime}`).join(' | ')}
                    </Text>
                  </div>
                  <Group gap="xs">
                    <ActionIcon variant="subtle" onClick={() => openEdit(schedule)}>
                      <IconEdit size={16} />
                    </ActionIcon>
                    {confirmDeleteId === schedule.id ? (
                      <Group gap={4}>
                        <Button size="compact-xs" variant="subtle" onClick={() => setConfirmDeleteId(null)}>
                          Cancel
                        </Button>
                        <Button size="compact-xs" color="red" onClick={() => void remove(schedule.id)}>
                          Delete
                        </Button>
                      </Group>
                    ) : (
                      <ActionIcon variant="subtle" color="red" onClick={() => setConfirmDeleteId(schedule.id)}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    )}
                  </Group>
                </Group>
              </Paper>
            );
          })}
        </Stack>
      )}

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingId ? 'Edit Schedule' : 'Create Schedule'}
        size="lg"
      >
        <Stack gap="md">
          <ScheduleForm form={form} onChange={setForm} />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => void save()} disabled={!form.name.trim()}>
              {editingId ? 'Save Changes' : 'Create'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

function SettingsBusinessHoursPage() {
  const { organizationId } = useOrg();

  if (!organizationId) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  return <BusinessHours organizationId={organizationId} />;
}
